// Piezas comunes de los PDFs del CMMS (certificado, reporte de visita): colores, bordes,
// encabezado de la empresa o membrete de fondo, firmas y marcas cumple / no cumple.
// pdfmake 0.1.71: ninguna tabla con body vacio, cada fila con tantas celdas como widths,
// widths nuevos en cada tabla (se mutan) y texto largo partible.

export const AZUL = "#1f3b57";
export const GRIS = "#5b6b7c";
export const LINEA = "#9aa7b4";
export const FONDO_ENCABEZADO = "#eef2f6";
export const VERDE = "#2e7d32";
export const ROJO = "#c62828";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

export const partible = (t) => String(t ?? "").replace(/(\S{18})/g, "$1​");

// Fecha-dia ("YYYY-MM-DD" o Date guardada a mediodia UTC) -> "07 oct 2026".
export const fechaDiaTexto = (f) => {
    if (!f) return "";
    const s = String(typeof f === "string" ? f : new Date(f).toISOString()).slice(0, 10);
    const [a, m, d] = s.split("-");
    return a && m && d ? `${d} ${MESES[Number(m) - 1] || ""} ${a}` : "";
};

export const bordes = {
    hLineWidth: () => 0.6,
    vLineWidth: () => 0.6,
    hLineColor: () => LINEA,
    vLineColor: () => LINEA,
    paddingLeft: () => 4,
    paddingRight: () => 4,
    paddingTop: () => 2.5,
    paddingBottom: () => 2.5,
};

export const tituloSeccion = (texto, margenArriba = 8) => ({ text: texto, bold: true, color: AZUL, fontSize: 9.5, margin: [0, margenArriba, 0, 3] });

export const dato = (etiqueta, valor) => ({ text: [{ text: `${etiqueta}: `, bold: true }, partible(valor || "")], margin: [0, 0, 0, 1.2] });

export const encabezadoTabla = (texto, extra = {}) => ({ text: texto, bold: true, fillColor: FONDO_ENCABEZADO, color: AZUL, ...extra });

// Palomita / tache dibujados (la fuente Roboto de pdfmake no trae ✓ ni ✗).
export const marca = (cumple, tam = 8) => {
    if (cumple === true)
        return { canvas: [{ type: "polyline", lineWidth: 1.4, lineColor: VERDE, closePath: false, points: [{ x: 0.5, y: tam * 0.55 }, { x: tam * 0.38, y: tam * 0.9 }, { x: tam, y: tam * 0.1 }] }], width: tam + 1 };
    if (cumple === false)
        return {
            canvas: [
                { type: "line", x1: 1, y1: 1, x2: tam - 1, y2: tam - 1, lineWidth: 1.4, lineColor: ROJO },
                { type: "line", x1: tam - 1, y1: 1, x2: 1, y2: tam - 1, lineWidth: 1.4, lineColor: ROJO },
            ],
            width: tam + 1,
        };
    return { text: "", width: tam + 1 };
};

// Resultado del checklist: 1 cumple · 2 no cumple · 3 no aplica.
export const marcaChecklist = (v) => {
    if (v === 3) return { text: "N/A", fontSize: 6.5, color: GRIS, alignment: "center" };
    if (v === 1 || v === 2) return { columns: [{ text: "", width: "*" }, marca(v === 1), { text: "", width: "*" }], margin: [0, 1, 0, 0] };
    return { text: "" };
};

// Valor con su marca a la derecha (para "valor entregado").
export const valorConMarca = (texto, cumple) => ({
    columns: [{ text: texto || "", width: "auto", color: cumple === false ? ROJO : undefined, bold: cumple === false }, cumple === null || cumple === undefined ? { text: "", width: 1 } : marca(cumple, 7)],
    columnGap: 3,
});

export const encabezadoEmpresa = (empresa) => {
    const e = empresa || {};
    return {
        columns: [
            e.logo ? { image: "logoEmpresa", fit: [90, 50], width: 100 } : { text: "", width: 0 },
            {
                stack: [
                    { text: e.nombre || "", bold: true, fontSize: 11, color: AZUL },
                    e.direccion ? { text: partible(e.direccion), fontSize: 7.5, color: GRIS } : "",
                    { text: [e.telefono ? `Tel. ${e.telefono}` : "", e.telefono && (e.paginaWeb || e.correo) ? " · " : "", e.paginaWeb || e.correo || ""].join(""), fontSize: 7.5, color: GRIS },
                ].filter(Boolean),
                margin: [8, 2, 0, 0],
            },
        ],
        margin: [0, 0, 0, 6],
    };
};

// Linea de firma del ancho de su columna (menos un margen igual a cada lado), asi queda
// centrada con el nombre de abajo sin importar cuantas firmas haya. Es el borde de arriba de
// una tabla de una celda: la linea mide lo que mide la columna.
export const lineaFirma = (margenArriba = 34, sangria = 14) => ({
    table: { widths: ["*"], body: [[{ text: "", fontSize: 1 }]] },
    layout: {
        hLineWidth: (i) => (i === 0 ? 0.8 : 0),
        vLineWidth: () => 0,
        hLineColor: () => "#333333",
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
    },
    margin: [sangria, margenArriba, sangria, 3],
});

// firmas: [{ etiqueta, nombre, detalle, imagen? }] — imagen = firma capturada en la app (data URI),
// se dibuja sobre la linea en el mismo alto que deja el espacio para firmar a mano.
export const lineasFirma = (firmas, { margenArriba = 34, tamEtiqueta = 7.5 } = {}) => ({
    columns: firmas.map((f) => ({
        width: "*",
        stack: [
            ...(f.imagen ? [{ image: f.imagen, fit: [150, margenArriba + 4], alignment: "center", margin: [0, 2, 0, -2] }] : []),
            lineaFirma(f.imagen ? 0 : margenArriba),
            { text: f.etiqueta, alignment: "center", fontSize: tamEtiqueta, bold: true },
            f.nombre ? { text: f.nombre, alignment: "center", fontSize: tamEtiqueta, color: GRIS } : null,
            f.detalle ? { text: f.detalle, alignment: "center", fontSize: tamEtiqueta - 0.5, color: GRIS } : null,
        ].filter(Boolean),
    })),
    columnGap: 16,
    unbreakable: true,
    margin: [0, 8, 0, 0],
});

// ─── Fondo de la hoja: membrete y marca de agua ──────────────────────────────

const LADO_MARCA_AGUA = 330;
const ANCHO_HOJA = 612;
const ALTO_HOJA = 792;

// Mide la marca de agua (para centrarla sin deformarla). Solo en el navegador; sin medidas
// se acomoda en un cuadro al centro.
export const medirMarcaAgua = async (datos) => {
    const src = datos?.impresion?.marcaAgua;
    if (!src || typeof Image === "undefined") return datos;
    const medidas = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ ancho: img.naturalWidth, alto: img.naturalHeight });
        img.onerror = () => resolve(null);
        img.src = src;
    });
    if (medidas?.ancho && medidas?.alto) datos.impresion.marcaAguaMedidas = medidas;
    return datos;
};

const capaMarcaAgua = (imp) => {
    const md = imp.marcaAguaMedidas;
    if (!md) return { image: "marcaAgua", fit: [LADO_MARCA_AGUA, LADO_MARCA_AGUA], opacity: 0.08, absolutePosition: { x: (ANCHO_HOJA - LADO_MARCA_AGUA) / 2, y: (ALTO_HOJA - LADO_MARCA_AGUA) / 2 } };
    const r = Math.min(LADO_MARCA_AGUA / md.ancho, LADO_MARCA_AGUA / md.alto);
    const ancho = md.ancho * r;
    const alto = md.alto * r;
    return { image: "marcaAgua", width: ancho, height: alto, opacity: 0.08, absolutePosition: { x: (ANCHO_HOJA - ancho) / 2, y: (ALTO_HOJA - alto) / 2 } };
};

// Pone en dd (images y background) el membrete de hoja completa y la marca de agua tenue al
// centro, los del cliente o, si no tiene, los de la empresa (los resuelve el backend).
export const aplicarFondo = (dd, imp = {}) => {
    const capas = [];
    dd.images = dd.images || {};
    if (imp.membrete) {
        dd.images.membrete = imp.membrete;
        capas.push({ image: "membrete", width: ANCHO_HOJA, height: ALTO_HOJA, absolutePosition: { x: 0, y: 0 } });
    }
    if (imp.marcaAgua) {
        dd.images.marcaAgua = imp.marcaAgua;
        capas.push(capaMarcaAgua(imp));
    }
    if (capas.length) dd.background = () => capas;
    return dd;
};

// Documento carta con membrete de fondo (cliente o empresa) o, sin el, encabezado de la empresa.
// pie: (actual, total) => texto de la derecha del pie.
export const documentoCMMS = ({ datos, content, pieIzquierda = "", pieDerecha, sinEncabezado = false }) => {
    const imp = datos.impresion || {};
    const conMembrete = !!imp.membrete;
    const m = imp.margenes || {};
    const dd = {
        pageSize: "LETTER",
        pageMargins: conMembrete ? [m.izquierda ?? 40, m.arriba ?? 110, m.derecha ?? 40, m.abajo ?? 90] : [32, 30, 32, 44],
        content: conMembrete || sinEncabezado ? content : [encabezadoEmpresa(datos.empresa), ...content],
        defaultStyle: { fontSize: 8, color: "#1f2a37" },
        images: {},
        footer: (actual, total) => ({
            columns: [
                { text: pieIzquierda, fontSize: 6.5, color: GRIS, margin: [32, 12, 0, 0] },
                { text: pieDerecha ? pieDerecha(actual, total) : `Página ${actual} de ${total}`, alignment: "right", fontSize: 6.5, color: GRIS, margin: [0, 12, 32, 0] },
            ],
        }),
    };
    aplicarFondo(dd, imp);
    if (!conMembrete && datos.empresa?.logo) dd.images.logoEmpresa = datos.empresa.logo;
    return dd;
};
